import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  ExternalSystemInstanceType,
  GlobalExternalSystemInstanceType,
} from '../../types/external-system-instance/external-system-instance.type';
import { ExternalSystemProvider } from '../../utils/constants';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import { EXTERNAL_SYSTEM_INSTANCE_TYPE } from '../../types/enums/external-system-instance-type.enum';
import { rejectWithApiError } from '../../utils/api';
import { MobileCarrierPlanConfig } from '../../types/external-system-instance/mobile-carrier-plan-config';
import { RootReducerState } from '../../redux/Reducers';
import { CSRPConfigType } from '../../types/external-system-instance/csrp-config.type';

export const getExternalSystems = createAsyncThunk<
  ExternalSystemInstanceType[],
  {
    type?: EXTERNAL_SYSTEM_INSTANCE_TYPE;
    provider?: ExternalSystemProvider;
    orgId: number;
    modelId?: number;
    telcoId?: number;
  },
  { state: RootReducerState; rejectValue: string }
>('externalSystems/getExternalSystems', async ({ type, provider, orgId, modelId, telcoId }, thunkAPI) => {
  try {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (provider) params.append('provider', provider);
    params.append('org_id', String(orgId));
    if (telcoId) params.append('telco_id', String(telcoId));
    if (modelId) params.append('model_id', String(modelId));
    const url = `${process.env.REACT_APP_API_URL}/external-systems?${params.toString()}`;
    const response = await axios.get<ExternalSystemInstanceType[]>(url);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting external systems!' });
  }
});

export const getGlobalExternalSystems = createAsyncThunk<
  GlobalExternalSystemInstanceType[],
  { orgId: number },
  { state: RootReducerState; rejectValue: string }
>('externalSystems/getGlobalExternalSystems', async ({ orgId }, thunkAPI) => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/external-systems/global?org_id=${orgId}`;
    const response = await axios.get<GlobalExternalSystemInstanceType[]>(url);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting external systems!' });
  }
});

export const getExternalSystemInstanceDetails = createAsyncThunk<
  ExternalSystemInstanceType,
  { id: number },
  { state: RootReducerState }
>('externalSystems/getExternalSystemInstanceDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<ExternalSystemInstanceType>(
      `${process.env.REACT_APP_API_URL}/external-systems/${id}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting external system!' });
  }
});

export const postExternalSystemDetails = createAsyncThunk<
  { success: boolean },
  { type: ExternalSystemProvider; dto: { organization: { id: number } } },
  { state: RootReducerState }
>('externalSystems/postExternalSystemDetails', async ({ type, dto }, thunkAPI) => {
  try {
    let url = '';
    if (type === ExternalSystemProvider.MEGASWITCH_PBX) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/megaswitch`;
    } else if (type === ExternalSystemProvider.NETSAPIENS_PBX) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/netsapiens`;
    } else if (type === ExternalSystemProvider.SKYSWITCH_PBX) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/skyswitch-pbx`;
    } else if (type === ExternalSystemProvider.QUESTBLUE) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/questblue`;
    } else if (type === ExternalSystemProvider.SKYSWITCH_TELCO) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/skyswitch-telco`;
    } else if (type === ExternalSystemProvider.ROUTE_TRUST_TELCO) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/routetrust`;
    } else if (type === ExternalSystemProvider.SINCH) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/sinch`;
    } else if (type === ExternalSystemProvider.ALIANZA_PBX) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/alianza-pbx`;
    } else if (type === ExternalSystemProvider.ALIANZA_TELCO) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/alianza-telco`;
    } else if (type === ExternalSystemProvider.MEGASWITCH_SYSTEM_PBX) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/megaswitch-system`;
    } else if (type === ExternalSystemProvider.VERIZON_MOBILE_CARRIER) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/verizon-mobile-carrier`;
    } else if (type === ExternalSystemProvider.TEAL_MOBILE_CARRIER) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/teal-mobile-carrier`;
    } else if (type === ExternalSystemProvider.GDMS_SERVICE) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/gdms`;
    } else if (type === ExternalSystemProvider.PRM_CRM_BRIDGE) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/prm-crm-bridge`;
    } else if (type === ExternalSystemProvider.CSRP_MEGASWITCH) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/csrp-megaswitch`;
    } else if (type === ExternalSystemProvider.BANDWIDTH) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/bandwidth`;
    } else if (type === ExternalSystemProvider.TECHLINK) {
      url = `${process.env.REACT_APP_API_URL}/external-systems/techlink`;
    }

    const response = await axios.post<ExternalSystemInstanceType>(url, dto);

    try {
      await axios.get(`${process.env.REACT_APP_API_URL}/external-systems/${response.data.id}/test`);
      thunkAPI.dispatch(getExternalSystems({ orgId: dto.organization.id }));
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'External System Credentials are not valid!' });
    }
    return { success: true };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating external systems!' });
  }
});

export const deleteExternalSystemDetails = createAsyncThunk<
  { success: boolean },
  { id: number; organizationID: number },
  { state: RootReducerState }
>('externalSystems/deleteExternalSystemDetails', async ({ id, organizationID }, thunkAPI) => {
  try {
    const response = await axios.delete<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/external-systems/${id}`);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(getExternalSystems({ orgId: organizationID }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting external systems!' });
  }
});

export const patchExternalSystemDetails = createAsyncThunk<
  ExternalSystemInstanceType,
  {
    id: number;
    organizationID: number;
    data: Partial<ExternalSystemInstanceType> & {
      enabledByDefault: boolean;
      inheritanceAvailable?: boolean;
      registerCDRs?: boolean;
    };
  },
  { state: RootReducerState }
>('externalSystems/patchExternalSystemDetails', async ({ data, organizationID, id }, thunkAPI) => {
  try {
    if (data.registerCDRs) {
      try {
        await axios.get(`${process.env.REACT_APP_API_URL}/netsapiens/${id}/createCDRCallSubscription`);
      } catch (error) {
        return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating CDR call subscription!' });
      }
    }
    const response = await axios.patch<ExternalSystemInstanceType>(
      `${process.env.REACT_APP_API_URL}/external-systems/${id}`,
      {
        ...data,
      }
    );

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(getExternalSystems({ orgId: organizationID }));
    thunkAPI.dispatch(getGlobalExternalSystems({ orgId: organizationID }));
    thunkAPI.dispatch(getExternalSystemsProviders({ organizationID: organizationID }));
    thunkAPI.dispatch(getExternalSystemsPBX({ organizationID: organizationID }));
    thunkAPI.dispatch(getExternalSystemMapperList({ organizationID: organizationID }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating external systems!' });
  }
});

export const patchGlobalExternalSystemDetails = createAsyncThunk<
  ExternalSystemInstanceType,
  { id: number; data: { enabled: boolean; organizationID: number } },
  { state: RootReducerState }
>('externalSystems/patchGlobalExternalSystemDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.post<ExternalSystemInstanceType>(
      `${process.env.REACT_APP_API_URL}/external-systems/${id}/${data.organizationID}?enable=${data.enabled}`,
      {}
    );

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(getExternalSystems({ orgId: data.organizationID }));
    thunkAPI.dispatch(getGlobalExternalSystems({ orgId: data.organizationID }));
    thunkAPI.dispatch(getExternalSystemsProviders({ organizationID: data.organizationID }));
    thunkAPI.dispatch(getExternalSystemsPBX({ organizationID: data.organizationID }));
    thunkAPI.dispatch(getExternalSystemMapperList({ organizationID: data.organizationID }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating global external systems!' });
  }
});

export const getExternalSystemMapperList = createAsyncThunk<
  Array<ExternalSystemInstanceType>,
  { organizationID: number },
  { state: RootReducerState }
>('externalSystems/getExternalSystemMapperList', async ({ organizationID }, thunkAPI) => {
  try {
    const response = await axios.get<Array<ExternalSystemInstanceType>>(
      `${process.env.REACT_APP_API_URL}/external-systems/routing-config/?org_id=${organizationID}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting external systems mapper list!' });
  }
});

export const getExternalCarrierMapperList = createAsyncThunk<
  MobileCarrierPlanConfig[],
  { organizationID: number },
  { state: RootReducerState; rejectValue: string }
>('externalSystems/getExternalCarrierMapperList', async ({ organizationID }, thunkAPI) => {
  try {
    const response = await axios.get<MobileCarrierPlanConfig[]>(
      `${process.env.REACT_APP_API_URL}/external-systems/carrier-plan-config?org_id=${organizationID}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting carrier systems mapper list!' });
  }
});

export const getCSRPConfigsList = createAsyncThunk<
  CSRPConfigType[],
  { organizationID: number },
  { state: RootReducerState; rejectValue: string }
>('externalSystems/getCSRPConfigsList', async ({ organizationID }, thunkAPI) => {
  try {
    const response = await axios.get<CSRPConfigType[]>(
      `${process.env.REACT_APP_API_URL}/external-systems/csrp-config`,
      {
        params: { org_id: organizationID },
      }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting csrp mapper list!' });
  }
});

export const getExternalSystemsProviders = createAsyncThunk<
  Array<ExternalSystemInstanceType>,
  { organizationID?: number },
  { state: RootReducerState }
>('externalSystems/getExternalSystemsProviders', async ({ organizationID }, thunkAPI) => {
  try {
    const response = await axios.get<Array<ExternalSystemInstanceType>>(
      `${process.env.REACT_APP_API_URL}/external-systems?type=TELCO&${organizationID ? `org_id=${organizationID}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting external provider systems!' });
  }
});

export const getExternalSystemsPBX = createAsyncThunk<
  Array<ExternalSystemInstanceType>,
  { organizationID?: number },
  { state: RootReducerState }
>('externalSystems/getExternalSystemsPBX', async ({ organizationID }, thunkAPI) => {
  try {
    const response = await axios.get<Array<ExternalSystemInstanceType>>(
      `${process.env.REACT_APP_API_URL}/external-systems?type=PBX&${organizationID ? `org_id=${organizationID}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting external PBX systems!' });
  }
});

export const postRouteConfig = createAsyncThunk<
  { success: boolean },
  { orgId: number; systemId: number; providerId: number; config: string; e911: boolean },
  { state: RootReducerState }
>('externalSystems/postRouteConfig', async ({ orgId, config, systemId, providerId, e911 }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/external-systems/routing-config?org_id=${orgId}`,
      {
        system: { id: systemId },
        provider: { id: providerId },
        e911_pbx: e911,
        config: config,
      }
    );
    thunkAPI.dispatch(getExternalSystemMapperList({ organizationID: orgId }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating external PBX systems!' });
  }
});

export const createCarrierPlanConfig = createAsyncThunk<
  { success: boolean },
  {
    orgId: number;
    mobileCarrier: { id: number };
    dataPlanType: { id: number };
    carrierPlanDisplayName: string;
    carrierPlanCode: string;
  },
  { state: RootReducerState; rejectValue: string }
>(
  'externalSystems/createCarrierPlanConfig',
  async ({ orgId, mobileCarrier, dataPlanType, carrierPlanDisplayName, carrierPlanCode }, thunkAPI) => {
    try {
      const response = await axios.post<{ success: boolean }>(
        `${process.env.REACT_APP_API_URL}/external-systems/carrier-plan-config?org_id=${orgId}`,
        {
          mobileCarrier,
          dataPlanType,
          carrierPlanDisplayName,
          carrierPlanCode,
        }
      );

      thunkAPI.dispatch(getExternalCarrierMapperList({ organizationID: orgId }));
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating carrier mapping!' });
    }
  }
);

export const createCSRPConfig = createAsyncThunk<
  { success: boolean },
  {
    orgId: number;
    telcoInstance: { id: number };
    pbxInstance: { id: number } | null;
    csrpInstance: { id: number };
    configuration: {
      e911Instance: { id: number; name: string } | null;
      defaultBillingGroup: { id: number; name: string } | null;
      rate: { id: number; name: string };
      tfRate: { id: number; name: string };
      vendor: { id: number; name: string };
      cosType: string;
      prefixRule: string;
      bandwidthSubAccount: { id: number; name: string } | null;
      bandwidthLocation: { id: number; name: string } | null;
      bandwidthVoiceConfiguration: { id: string; name: string } | null;
      countries: Array<{ id: number; name: string }>;
      numberTypes: string[];
    };
  },
  { state: RootReducerState; rejectValue: string }
>('externalSystems/createCSRPConfig', async (dto, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/external-systems/csrp-config`,
      dto
    );

    thunkAPI.dispatch(getCSRPConfigsList({ organizationID: dto.orgId }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating csrp mapping!' });
  }
});

export const updateCSRPConfig = createAsyncThunk<
  { success: boolean },
  {
    id: number;
    orgId: number;
    dto: {
      e911Instance: { id: number; name: string } | null;
      defaultBillingGroup: { id: number; name: string } | null;
      rate: { id: number; name: string };
      tfRate: { id: number; name: string };
      vendor: { id: number; name: string };
      cosType: string;
      prefixRule: string;
      bandwidthSubAccount: { id: number; name: string } | null;
      bandwidthLocation: { id: number; name: string } | null;
      bandwidthVoiceConfiguration: { id: string; name: string } | null;
      countries: Array<{ id: number; name: string }>;
      numberTypes: string[];
    };
  },
  { state: RootReducerState; rejectValue: string }
>('externalSystems/updateCSRPConfig', async ({ id, dto, orgId }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/external-systems/csrp-config/${id}`,
      dto
    );
    thunkAPI.dispatch(getCSRPConfigsList({ organizationID: orgId }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating csrp config!' });
  }
});

export const deleteCarrierPlanConfig = createAsyncThunk<
  { success: boolean },
  { orgId: number; configId: number },
  { state: RootReducerState; rejectValue: string }
>('externalSystems/deleteCarrierPlanConfig', async ({ orgId, configId }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/external-systems/carrier-plan-config/${configId}`
    );
    thunkAPI.dispatch(getExternalCarrierMapperList({ organizationID: orgId }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting carrier mapping!' });
  }
});

export const deleteRouteConfig = createAsyncThunk<
  { success: boolean },
  { orgId: number; configId: number },
  { state: RootReducerState }
>('externalSystems/deleteRouteConfig', async ({ orgId, configId }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/external-systems/routing-config/${configId}`
    );
    thunkAPI.dispatch(getExternalSystemMapperList({ organizationID: orgId }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting external systems routing config!' });
  }
});

export const deleteCSRPConfig = createAsyncThunk<
  { success: boolean },
  { orgId: number; configId: number },
  { state: RootReducerState }
>('externalSystems/deleteCSRPConfig', async ({ orgId, configId }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/external-systems/csrp-config/${configId}`
    );

    thunkAPI.dispatch(getCSRPConfigsList({ organizationID: orgId }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting csrp config!' });
  }
});

export const patchRouteConfig = createAsyncThunk<
  { success: boolean },
  { orgId: number; configId: number; config: string; e911: boolean },
  { state: RootReducerState }
>('externalSystems/patchRouteConfig', async ({ orgId, config, configId, e911 }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/external-systems/routing-config/${configId}`,
      {
        config: config,
        e911_pbx: e911,
      }
    );
    thunkAPI.dispatch(getExternalSystemMapperList({ organizationID: orgId }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating external systems routing config!' });
  }
});

export const updateCarrierPlanConfig = createAsyncThunk<
  { success: boolean },
  { orgId: number; configId: number; carrierPlanDisplayName: string; carrierPlanCode: string },
  { state: RootReducerState; rejectValue: string }
>(
  'externalSystems/updateCarrierPlanConfig',
  async ({ orgId, configId, carrierPlanDisplayName, carrierPlanCode }, thunkAPI) => {
    try {
      const response = await axios.patch<{ success: boolean }>(
        `${process.env.REACT_APP_API_URL}/external-systems/carrier-plan-config/${configId}`,
        { carrierPlanDisplayName, carrierPlanCode }
      );
      thunkAPI.dispatch(getExternalCarrierMapperList({ organizationID: orgId }));
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating carrier config!' });
    }
  }
);

export const getPBXDomainsList = createAsyncThunk<
  Array<{
    description: string;
    domain: string;
  }>,
  { externalSystemId: number },
  { state: RootReducerState }
>('externalSystems/getPBXDomainsList', async ({ externalSystemId }, thunkAPI) => {
  try {
    const response = await axios.get<
      Array<{
        description: string;
        domain: string;
      }>
    >(`${process.env.REACT_APP_API_URL}/external-systems/getAvailableDomains?id=${externalSystemId}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting domains list!' });
  }
});

export const postPBXDomains = createAsyncThunk<
  { success: boolean },
  {
    externalSystemInstance: { id: number };
    organization: { id: number };
    domains: string[];
  },
  { state: RootReducerState }
>('externalSystems/postPBXDomains', async (data, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/external-systems/importDomains`,
      data
    );
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(setShowModal(false));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during importing domains!' });
  }
});
